/**
 * Display formatting for ClinicalTrials.gov phase and status values.
 *
 * The API stores the raw CT.gov enums (`PHASE1`, `ACTIVE_NOT_RECRUITING`) so
 * they stay canonical for filtering and matching. These helpers turn them into
 * the human-readable labels CT.gov itself uses, for the profile UI only. Labels
 * come from the CT.gov v2 enums endpoint (`/api/v2/studies/enums`). Both helpers
 * are idempotent and fall back to a generic transform for unrecognized values.
 */

/** Canonical CT.gov phase labels. NA is omitted: it is filtered upstream and not shown. */
const PHASE_LABELS: Record<string, string> = {
  EARLY_PHASE1: 'Early Phase 1',
  PHASE1: 'Phase 1',
  PHASE2: 'Phase 2',
  PHASE3: 'Phase 3',
  PHASE4: 'Phase 4',
};

/** Canonical CT.gov overall-status labels. */
const STATUS_LABELS: Record<string, string> = {
  ACTIVE_NOT_RECRUITING: 'Active, not recruiting',
  COMPLETED: 'Completed',
  ENROLLING_BY_INVITATION: 'Enrolling by invitation',
  NOT_YET_RECRUITING: 'Not yet recruiting',
  RECRUITING: 'Recruiting',
  SUSPENDED: 'Suspended',
  TERMINATED: 'Terminated',
  WITHDRAWN: 'Withdrawn',
  AVAILABLE: 'Available',
  NO_LONGER_AVAILABLE: 'No longer available',
  TEMPORARILY_NOT_AVAILABLE: 'Temporarily not available',
  APPROVED_FOR_MARKETING: 'Approved for marketing',
  WITHHELD: 'Withheld',
  UNKNOWN: 'Unknown status',
};

/** Sentence-cases a SCREAMING_SNAKE_CASE value as a fallback for unknown enums. */
const toSentenceCase = (value: string): string => {
  const sentence = value
    .trim()
    .split(/[_\s]+/)
    .join(' ')
    .toLowerCase()
    .trim();

  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

/**
 * Formats a CT.gov phase enum for display, e.g. "PHASE1" -> "Phase 1",
 * "PHASE1/PHASE2" -> "Phase 1/2". Returns null when the input is empty.
 */
export function formatTrialPhase(phase: string | null | undefined): string | null {
  if (!phase) {
    return null;
  }

  const segments = phase
    .split('/')
    .map((segment) => {
      const key = segment.trim().toUpperCase().replace(/\s+/g, '');

      return PHASE_LABELS[key] ?? (key ? toSentenceCase(segment) : '');
    })
    .filter(Boolean);

  if (segments.length === 0) {
    return null;
  }

  // "Phase 1" and "Phase 2" read as one phase, "Phase 1/2". A segment like "Early Phase 1"
  // keeps its words, so it joins whole.
  const numbered = segments.map((segment) => /^Phase (\d)$/.exec(segment)?.[1]);

  if (segments.length > 1 && numbered.every(Boolean)) {
    return `Phase ${numbered.join('/')}`;
  }

  return segments.join('/');
}

/**
 * Formats a CT.gov status enum for display, e.g. "ACTIVE_NOT_RECRUITING" ->
 * "Active, not recruiting". Returns null when the input is empty.
 */
export function formatTrialStatus(status: string | null | undefined): string | null {
  if (!status) {
    return null;
  }

  return STATUS_LABELS[status.trim().toUpperCase()] ?? toSentenceCase(status);
}

export type TrialStatusTone = 'success' | 'error' | 'muted';

/**
 * Statuses of a trial that is running, still enrolling or enrolled and under way, plus an
 * expanded access program that is open to patients.
 */
const RUNNING_STATUSES = new Set([
  'RECRUITING',
  'ENROLLING_BY_INVITATION',
  'ACTIVE_NOT_RECRUITING',
  'AVAILABLE',
]);

/**
 * Statuses of a trial that has stopped before finishing as planned. A withdrawn trial
 * stopped before it enrolled anyone, so it ends early as a terminated one does.
 */
const STOPPED_STATUSES = new Set(['SUSPENDED', 'TERMINATED', 'WITHDRAWN']);

/**
 * The tone of a trial's status badge: green while the trial is running, red once it has
 * been suspended, terminated or withdrawn, and gray for everything else, including
 * completed, not yet recruiting and unknown.
 *
 * The badge always shows the exact status, so green on "Active, not recruiting" does not
 * claim enrollment is open.
 */
export function trialStatusTone(status: string | null | undefined): TrialStatusTone {
  const key = status
    ? status
        .trim()
        .toUpperCase()
        .replace(/[,\s-]+/g, '_')
    : '';

  if (RUNNING_STATUSES.has(key)) {
    return 'success';
  }

  return STOPPED_STATUSES.has(key) ? 'error' : 'muted';
}
